import { useLocalStorageState } from 'ahooks';
import { Carousel, Modal, Typography } from 'antd';
import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NoticeInfoIcon from '@/assets/images/icons/notice-info.react.svg?react';
import { useAppConfig } from '@/config/context';
import { getUserId } from '@/infrastructure/auth';
import { logger } from '@/infrastructure/logger';
import { getPlatform, getProductId } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { useAuthContext } from '@/presentation/hooks';
import { useStores } from '@/presentation/store';
import styles from './index.module.scss';

interface NoticeInfoType {
  _id?: string;
  _mtime?: string;
  mainTitle?: string;
  functionTitle?: string;
  desc?: string;
  detailLink?: string;
  imageUrl?: string[];
  expireTime?: string;
  hasClosed?: boolean;
  whiteList?: string[];
  appVersion?: string[];
  isLinkNeedModify?: boolean;
}

interface OperationNoticeProps {
  liteModeRoutes?: string[];
}

export const OperationNotice: React.FC<OperationNoticeProps> = ({
  liteModeRoutes = ['/workbench/apps', '/user/profile', '/config-manage', '/git/oauth', '/'],
}) => {
  const { pgid } = useStores().LayoutStore;
  const { authInfo } = useAuthContext();
  const location = useLocation();
  const config = useAppConfig();
  const { Paragraph } = Typography;

  const [currPlatform, setCurrPlatform] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noticeInfo, setNoticeInfo] = useLocalStorageState<Record<string, NoticeInfoType>>(
    'notice-info-storage',
    { listenStorageChange: true },
  );
  const [latestNoticeInfo, setLatestNoticeInfo] = useLocalStorageState<Record<string, string>>(
    'latest-noticeInfo-storage',
    { listenStorageChange: true },
  );

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setNoticeInfo({
      ...noticeInfo,
      [currPlatform as string]: {
        ...noticeInfo?.[currPlatform as string],
        hasClosed: true,
      },
    });
    setIsModalOpen(false);
    reportClick(pgid, CUSTOM_ACTIONS.noticeModalView, {
      from: pgid,
    });
  };

  const viewDetail = () => {
    let modifiedUrl: string;
    if (noticeInfo?.[currPlatform as string]?.isLinkNeedModify) {
      // 替换域名和productId
      try {
        const parsedUrl = new URL(noticeInfo?.[currPlatform as string]?.detailLink || '');
        parsedUrl.hostname = window.location.hostname;
        const params = new URLSearchParams(parsedUrl.search);
        if (params.has('productId')) {
          const productId = getProductId();
          params.set('productId', productId);
          parsedUrl.search = params.toString();
        }
        modifiedUrl = parsedUrl.toString();
      } catch (error) {
        logger.error('无效的 URL', error);
      }
    } else {
      modifiedUrl = noticeInfo?.[currPlatform as string]?.detailLink as string;
    }
    setIsModalOpen(false);
    setTimeout(() => window.open(modifiedUrl, '_blank'), 100);
    setNoticeInfo({
      ...noticeInfo,
      [currPlatform as string]: {
        ...noticeInfo?.[currPlatform as string],
        hasClosed: true,
      },
    });
    reportClick(pgid, CUSTOM_ACTIONS.noticeModalViewDetail, {
      from: pgid,
    });
  };

  const noticeInfoQuery = async () => {
    const noticeUrlObj = new URL(config.noticeRequestURL);
    const searchParams = new URLSearchParams(noticeUrlObj.search);
    const filter = searchParams.get('filter'); // 获取注入url的filter参数

    const currTime = moment().unix();
    searchParams.set('filter', `expireTime>${currTime}&${filter}`); // 更新filter参数包含过期时间的筛选

    noticeUrlObj.search = searchParams.toString();
    const noticeURL = noticeUrlObj.toString();

    try {
      const res = await axios.get(noticeURL, { withCredentials: true });
      return res.data;
    } catch {
      return null;
    }
  };

  const load = async () => {
    if (liteModeRoutes.includes(location.pathname)) {
      // 用户没有进到具体的产品页直接不请求
      return;
    }
    const originNoticeInfo = await noticeInfoQuery();

    if (originNoticeInfo.data?.length === 0) {
      return;
    }
    // eslint-disable-next-line max-len
    const lastModifyItem = originNoticeInfo?.data?.filter((info: any) => info?.platform?.includes(getPlatform()))?.[0];

    const whiteList = lastModifyItem?.whiteListForOA;
    if (
      !lastModifyItem // 管理台没有设置弹窗内容
      || (whiteList?.length !== 0 && !whiteList?.includes(getUserId())) // 用户不在白名单
    ) {
      return;
    }

    if (
      (!noticeInfo?.[currPlatform as string]
        && (lastModifyItem?._id !== latestNoticeInfo?._id
          || lastModifyItem?._mtime !== latestNoticeInfo?._mtime)) // 没有历史通知信息且最新通知用户未看过
      || (noticeInfo?.[currPlatform as string]
        && (((lastModifyItem?._id !== noticeInfo?.[currPlatform as string]?._id
          || lastModifyItem?._mtime !== noticeInfo?.[currPlatform as string]?._mtime)
          && (lastModifyItem?._id !== latestNoticeInfo?._id
            || lastModifyItem?._mtime !== latestNoticeInfo?._mtime))
          || !noticeInfo?.[currPlatform as string]?.hasClosed))
      // 拉取到的最新通知信息和存储的通知信息对比 须满足「{id不同或者同一id但被修改过}且{该消息在其他平台未被用户看到过}」或者「相同的弹窗但之前用户没有关闭」
    ) {
      setIsModalOpen(true);
      try {
        setNoticeInfo({
          ...noticeInfo,
          [currPlatform as string]: lastModifyItem,
        });
        setLatestNoticeInfo({
          ...latestNoticeInfo,
          _id: lastModifyItem?._id,
          _mtime: lastModifyItem?._mtime,
        });
      } catch {
        logger.error();
      }
    }
  };

  useEffect(() => {
    setCurrPlatform(getPlatform());
  }, [getPlatform()]);

  useEffect(() => {
    if (authInfo?.isLoggedIn) {
      load();
    }
  }, [currPlatform, location.pathname]);

  // 如果用户未登录，不渲染组件
  if (!authInfo?.isLoggedIn) {
    return null;
  }

  return (
    <Modal
      visible={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      className={styles.modal}
      footer={null}
      maskClosable={false}
      keyboard={false}
      centered={true}
    >
      <div className={styles.modalBox}>
        <div className={styles.leftBox}>
          <div className={styles.mainTitle}>
            <NoticeInfoIcon />
            <span>{noticeInfo?.[currPlatform as string]?.mainTitle}</span>
          </div>
          <div className={styles.functionTitle}>
            {noticeInfo?.[currPlatform as string]?.functionTitle}
          </div>
          <Paragraph className={styles.desc}>
            <div
              dangerouslySetInnerHTML={{
                __html: noticeInfo?.[currPlatform as string]?.desc || '',
              }}
            />
          </Paragraph>
          <div className={styles.viewDetailBox} onClick={viewDetail}>
            <span className={styles.viewDetailText}>查看详情</span>
          </div>
        </div>
        <div className={styles.rightSlide}>
          <Carousel autoplay dots={true}>
            {noticeInfo?.[currPlatform as string]?.imageUrl?.map?.((url, idx) => (
              <img src={url || ''} key={idx} alt={`notice-${idx}`} />
            ))}
          </Carousel>
        </div>
      </div>
    </Modal>
  );
};
